import { usePopUpsV2 } from "components/PopUpManagerV2/hooks/usePopUpsV2";
import { useEnvContext, useWebStoreContext } from "contexts";
import { FeaturesFlags } from "helpers";
import { useAppSelector, useLinks } from "hooks";
import { FeatureType } from "interfaces/feature-tracker";
import { SignUpIncentiveResponse } from "interfaces/sign-up-incentive";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { featureTrackerAPI } from "redux/apis";
import { RootState } from "redux/store";
import { useDialogModalRedux } from "../redux/slices/dialog/hooks/useDialogModalRedux";
import { selectDipState } from "../redux/slices/dip/index";
import { selectPopUpManagerState } from "../redux/slices/pop-up-manager";

/** priorities:
	InAPP dip
	welcomeflow
	ftues
	EE
*/

export const usePopupsState = () => {
	const links = useLinks();
	const { pathname } = useLocation();
	const { isFeatureFlagEnabled } = useEnvContext();
	const { variant: dialogVariant } = useDialogModalRedux();
	const {
		opened: isDipOpened,
		triggerCached,
		sessionStartDipStatus
	} = useAppSelector(selectDipState);
	const { authToken, userTracker, activeItemToBeClicked } =
		useWebStoreContext();
	const { variant: popupManagerVariant } = useAppSelector(
		selectPopUpManagerState
	);
	const { stack: popupManagerV2Variants } = usePopUpsV2();
	const isMegaMenuOpen = useSelector(
		(state: RootState) => state.megaMenu.isOpen
	);
	const { isChecking: IsInAppToWebDipChecking } = useAppSelector(
		(state: RootState) => state.inAppToWebDip
	);
	const { isCheckingFr: IsFriendReferralAcceptInviteChecking } = useAppSelector(
		(state: RootState) => state.friendReferral
	);
	const { data: signupData }: { data?: SignUpIncentiveResponse } =
		featureTrackerAPI.endpoints.getFeatureTracker.useQueryState({
			FeatureType: FeatureType.SIGN_UP_INCENTIVE,
			token: authToken?.value
		});

	const isSignupChecking = signupData?.showIncentivePopup;

	const isFtueV2Enabled = isFeatureFlagEnabled(FeaturesFlags.FTUE_V2);
	const isDIPsEnabled = isFeatureFlagEnabled(FeaturesFlags.DIPS);
	const isDipPriorityPending =
		isFtueV2Enabled && isDIPsEnabled && sessionStartDipStatus !== "resolved";
	const isAnyDipOpen = isDipOpened || triggerCached === "sessionStart";

	const isAnyPopupOpen =
		!!popupManagerVariant ||
		!!dialogVariant ||
		isDipOpened ||
		popupManagerV2Variants.length > 0;

	const hasSeenShopTutorial = isFtueV2Enabled
		? userTracker?.metadata?.hasSeenTutorial === "true"
		: (isFeatureFlagEnabled(FeaturesFlags.UI_V2) &&
				userTracker?.metadata?.hasSeenTutorial === "true") ||
		  !isFeatureFlagEnabled(FeaturesFlags.UI_V2);
	const hasSeenLPTutorial = isFtueV2Enabled
		? userTracker?.metadata?.hasSeenLPTutorial === "true"
		: (isFeatureFlagEnabled(FeaturesFlags.LOYALTY_SHOP_FTUE) &&
				userTracker?.metadata?.hasSeenLPTutorial === "true") ||
		  !isFeatureFlagEnabled(FeaturesFlags.LOYALTY_SHOP_FTUE);

	const hasSeenLegacyTutorials = hasSeenShopTutorial && hasSeenLPTutorial;

	const isWelcomeFlowActive = userTracker?.isFirstLogin;

	const hasSeenRequiredPageTutorial = !isFtueV2Enabled
		? hasSeenLegacyTutorials
		: pathname === links.loyalty.path
		? hasSeenLPTutorial
		: hasSeenShopTutorial;

	const hasSeenEverything =
		!isAnyPopupOpen &&
		!isSignupChecking &&
		!isWelcomeFlowActive &&
		!activeItemToBeClicked &&
		!IsInAppToWebDipChecking &&
		hasSeenRequiredPageTutorial &&
		!IsFriendReferralAcceptInviteChecking;

	return {
		dialogVariant,
		hasSeenLegacyTutorials,
		hasSeenRequiredPageTutorial,
		isAnyPopupOpen,
		IsInAppToWebDipChecking,
		IsFriendReferralAcceptInviteChecking,
		isSignupChecking,
		isWelcomeFlowActive,
		popupManagerVariant,
		isMegaMenuOpen,
		hasSeenEverything,
		isAnyDipOpen,
		isDipPriorityPending
	};
};
